/**
 * EN↔DE Language Pair Definition
 *
 * buildProfile() generates directional profiles:
 *   enDe = DE native → learn EN (UI in German)
 *   deEn = EN native → learn DE (UI in English)
 */

import type { LanguagePairDefinition, LanguageProfile } from '../types'
import { buildProfile } from '../build-profile'

const PAIR: LanguagePairDefinition = {
  id: 'en-de',
  languages: {
    en: {
      lang: 'en',
      langName: 'English',
      shortCode: 'EN',
      scriptSample: 'Hello',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'US' },
          { key: 'phonetic_uk', label: 'UK' },
        ],
      },
      tts: {
        voices: [
          { accent: 'us', lang: 'en-US' },
          { accent: 'uk', lang: 'en-GB' },
        ],
        detectScript: /^$/,
      },
      ui: {
        inputPlaceholder: 'Enter words, phrases, or questions...',
        headingText: 'What do you want to learn today?',
        intentChips: [
          { label: 'List', intent: 'BATCH_LIST', suffix: 'how to say in German' },
          { label: 'Compare', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in English.',
      },
    },
    de: {
      lang: 'de',
      langName: 'German',
      shortCode: 'DE',
      scriptSample: 'Hallo',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'DE' },
        ],
      },
      tts: {
        voices: [
          { accent: 'de', lang: 'de-DE' },
        ],
        detectScript: /[a-zäöüß]/i,
      },
      ui: {
        inputPlaceholder: 'Wörter, Sätze oder Fragen eingeben...',
        headingText: 'Was möchtest du heute lernen?',
        intentChips: [
          { label: 'Liste', intent: 'BATCH_LIST', suffix: 'wie sagt man auf Englisch' },
          { label: 'Vergleichen', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in German.',
        languageHints: [
          'For nouns, always include the definite article (der/die/das) in "front" to indicate gender.',
          'For verbs with prepositions, indicate the case governed (Akkusativ/Dativ) in "definition".',
          'For separable prefix verbs (e.g. an|rufen), mark the prefix with | and show separated form in example.',
          'For long compound nouns, break down the components in "definition".',
        ].join('\n'),
      },
      cardMeta: {
        keys: ['gender', 'plural', 'separablePrefix'],
        promptDescription: `"card_meta" (JSON object, required): German-specific metadata:
- "gender": "m", "f", or "n". Only for nouns.
- "plural": plural form. Only for nouns.
- "separablePrefix": the separable prefix (e.g. "an" for "anrufen"). Only for separable verbs.`,
      },
    },
  },
}

export const enDePair = PAIR
export const enDe: LanguageProfile = buildProfile(PAIR, 'en')
export const deEn: LanguageProfile = buildProfile(PAIR, 'de')
